/*
 * VoiceWaveform — a live, branded waveform pinned to the composer's top border
 * while recording. Reads real mic levels off the AnalyserNode (falls back to a
 * gentle idle shimmer if the analyser isn't available). Brand: yellow→blue bars
 * (#ffea00 → #18191d). Purely cosmetic; never throws.
 */
import React, { useEffect, useRef } from 'react';

interface Props {
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
  active: boolean;
}

export function VoiceWaveform({ analyserRef, active }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const BARS = 56;
    const GAP = 2;

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      ctx.clearRect(0, 0, w, h);

      const analyser = analyserRef.current;
      let freq: Uint8Array | null = null;
      if (analyser) {
        freq = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(freq);
      }

      const barW = Math.max(1.5, (w - GAP * (BARS - 1)) / BARS);
      for (let i = 0; i < BARS; i++) {
        let amp: number;
        if (freq) {
          // Sample across the low–mid band where speech energy lives.
          const idx = Math.floor((i / BARS) * (freq.length * 0.7));
          amp = (freq[idx] || 0) / 255;
        } else {
          amp = 0.15 + 0.12 * Math.abs(Math.sin(Date.now() / 220 + i * 0.5));
        }
        const barH = Math.max(2, amp * (h - 2));
        const x = i * (barW + GAP);
        const y = (h - barH) / 2;
        const grad = ctx.createLinearGradient(0, y, 0, y + barH);
        grad.addColorStop(0, '#ffea00');
        grad.addColorStop(1, '#18191d');
        ctx.fillStyle = grad;
        const r = Math.min(barW / 2, 2);
        if (typeof ctx.roundRect === 'function') {
          ctx.beginPath();
          ctx.roundRect(x, y, barW, barH, r);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, barW, barH);
        }
      }
    };
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [active, analyserRef]);

  if (!active) return null;
  return <canvas ref={canvasRef} className="aa-composer-waveform" aria-hidden="true" />;
}

export default VoiceWaveform;
